import {
  lazy,
  Suspense,
  useMemo,
  useLayoutEffect,
  useState,
  useContext,
} from "react"
import { useSelector } from "react-redux"
import { createPortal } from "react-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Pencil, Plus, Search } from "lucide-react"
import { driversProfiles } from "@/reducers/profilesSliceSelectors"
import { participantPhotoToSrc } from "@/components/AvatarImage"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useAppSelector } from "@/reducers/hooks"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import { selectAccountChatsAsList } from "@/reducers/accountChatsSlice"
import { Profile } from "@/reducers/profilesSlice"
import EditDriverProfileModal from "./EditDriverProfileModal"
import { XmppContext } from "@/stream/xmppClient"
import { extractVhostFromJid } from "@/jidUtils"
import { RootState } from "@/reducers/store"
import { Input } from "@/components/ui/input"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { useDriverNameOrder } from "@/hooks/useDriverNameOrder"
import {
  compareDriverNames,
  driverName,
  type DriverNameOrder,
} from "@/lib/driverNameOrder"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import { useTranslation } from "react-i18next"

// Only a fleet manager opens it, so the chart library loads on that first visit.
const DispatcherBoardView = lazy(
  () => import("@/components/dispatcherBoard/DispatcherBoardView"),
)

type DriverManagementView = "drivers" | "dispatchers"

function getInitials(name: string, firstName?: string, lastName?: string) {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase()
  }
  const parts = name.split(" ")
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

const languageFlags: Record<string, string> = {
  pl: "\u{1F1F5}\u{1F1F1}",
  en: "\u{1F1EC}\u{1F1E7}",
  uk: "\u{1F1FA}\u{1F1E6}",
  ru: "\u{1F1F7}\u{1F1FA}",
  be: "\u{1F1E7}\u{1F1FE}",
  ro: "\u{1F1F7}\u{1F1F4}",
  hy: "\u{1F1E6}\u{1F1F2}",
  az: "\u{1F1E6}\u{1F1FF}",
  lt: "\u{1F1F1}\u{1F1F9}",
  lv: "\u{1F1F1}\u{1F1FB}",
  et: "\u{1F1EA}\u{1F1EA}",
  kk: "\u{1F1F0}\u{1F1FF}",
  uz: "\u{1F1FA}\u{1F1FF}",
  ky: "\u{1F1F0}\u{1F1EC}",
  tk: "\u{1F1F9}\u{1F1F2}",
  tg: "\u{1F1F9}\u{1F1EF}",
  de: "\u{1F1E9}\u{1F1EA}",
  fr: "\u{1F1EB}\u{1F1F7}",
  es: "\u{1F1EA}\u{1F1F8}",
  it: "\u{1F1EE}\u{1F1F9}",
}

export function DriverManagement() {
  const { t } = useTranslation()
  const drivers = useSelector(driversProfiles)
  const [isOpen, setIsOpen] = useState(false)
  const [editingDriver, setEditingDriver] = useState<Profile | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [filter, setFilter] = useState("")
  const { activeTab } = useAppSelector(selectTabs)
  const accountChats = useAppSelector(selectAccountChatsAsList)
  const cars = useAppSelector((state: RootState) => state.cars)
  const { client } = useContext(XmppContext)
  const { order: nameOrder, save: saveNameOrder } = useDriverNameOrder()
  const { isFleetManager } = useUserPermissions()
  const [selectedView, setView] = useState<DriverManagementView>("drivers")
  // Losing the role while the chart is open falls back to the driver list.
  const view = isFleetManager ? selectedView : "drivers"

  // Create a map of driverA jid -> car label
  const driverToVehicleName = useMemo(() => {
    const map = new Map<string, string>()
    accountChats.forEach((chat) => {
      if (chat.driverA && chat.carId) {
        const car = cars[chat.carId]
        if (car) {
          map.set(chat.driverA, car.plates || car.label)
        }
      }
    })
    return map
  }, [accountChats, cars])

  useLayoutEffect(() => {
    if (activeTab === TAB_TYPES.DRIVER_MANAGEMENT) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [activeTab])

  const sortedDrivers = useMemo(() => {
    const myVhost = client?.jid?.toString()
      ? extractVhostFromJid(client.jid.toString())
      : null

    return [...drivers]
      .filter((driver) => {
        if (!myVhost) return true
        const driverVhost = extractVhostFromJid(driver.jid)
        return driverVhost === myVhost
      })
      .sort(compareDriverNames(nameOrder))
  }, [drivers, client?.jid, nameOrder])

  const filteredDrivers = useMemo(() => {
    const q = filter.toLowerCase()
    if (!q) return sortedDrivers
    return sortedDrivers.filter((driver) => {
      // Both orders, so "jan kow" finds Kowalski Jan whichever way it reads.
      const names = [
        driverName(driver, "first_last"),
        driverName(driver, "last_first"),
      ].map((name) => name.toLowerCase())
      const phone = (driver.phone || "").toLowerCase()
      const vehicle = (driverToVehicleName.get(driver.jid) || "").toLowerCase()
      return (
        names.some((name) => name.includes(q)) ||
        phone.includes(q) ||
        vehicle.includes(q)
      )
    })
  }, [sortedDrivers, filter, driverToVehicleName])

  return (
    <>
      {isOpen &&
        createPortal(
          <div className="fixed inset-0 left-[49px] z-50 flex w-[100vw-48px] items-center justify-center rounded-md bg-background">
            <div className="relative h-full w-full">
              <div className="h-full w-full overflow-auto">
                <div className="p-6">
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-semibold">
                        {t("fleet.driverManagement.title")}
                      </h1>
                      <p className="text-sm text-muted-foreground">
                        {t("fleet.driverManagement.subtitle")}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {t("fleet.driverManagement.names")}
                        </span>
                        <ToggleGroup
                          type="single"
                          variant="outline"
                          size="sm"
                          value={nameOrder}
                          onValueChange={(value) => {
                            // Clicking the active item clears a single group.
                            if (value)
                              void saveNameOrder(value as DriverNameOrder)
                          }}
                        >
                          <ToggleGroupItem
                            value="first_last"
                            aria-label={t(
                              "fleet.driverManagement.firstNameFirst",
                            )}
                          >
                            {t("fleet.driverManagement.firstLast")}
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="last_first"
                            aria-label={t(
                              "fleet.driverManagement.lastNameFirst",
                            )}
                          >
                            {t("fleet.driverManagement.lastFirst")}
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </div>
                      {view === "drivers" && (
                        <Button
                          onClick={() => setIsAddModalOpen(true)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          {t("fleet.driverManagement.addDriver")}
                        </Button>
                      )}
                    </div>
                  </div>

                  {isFleetManager && (
                    <Tabs
                      value={view}
                      onValueChange={(value) =>
                        setView(value as DriverManagementView)
                      }
                      className="mb-4"
                    >
                      <TabsList>
                        <TabsTrigger value="drivers">
                          {t("fleet.driverManagement.driversTab")}
                        </TabsTrigger>
                        <TabsTrigger value="dispatchers">
                          {t("fleet.driverManagement.dispatchersTab")}
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  )}

                  {view === "dispatchers" ? (
                    <div className="h-[calc(100vh-11rem)] min-h-[420px]">
                      <Suspense fallback={null}>
                        <DispatcherBoardView />
                      </Suspense>
                    </div>
                  ) : (
                    <>
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder={t("fleet.driverManagement.filter")}
                          value={filter}
                          onChange={(e) => setFilter(e.target.value)}
                          className="pl-9"
                          autoFocus
                        />
                      </div>

                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[50px]"></TableHead>
                              <TableHead>
                                {t("fleet.driverManagement.name")}
                              </TableHead>
                              <TableHead>
                                {t("fleet.driverManagement.phone")}
                              </TableHead>
                              <TableHead>
                                {t("fleet.driverManagement.language")}
                              </TableHead>
                              <TableHead>
                                {t("fleet.driverManagement.vehicle")}
                              </TableHead>
                              <TableHead className="w-[80px]">
                                {t("fleet.driverManagement.actions")}
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredDrivers.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  className="h-24 text-center text-muted-foreground"
                                >
                                  {t("fleet.driverManagement.empty")}
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredDrivers.map((driver) => {
                                const photoSrc = participantPhotoToSrc(
                                  driver.photo,
                                )
                                const displayName = driverName(
                                  driver,
                                  nameOrder,
                                )
                                const initials = getInitials(
                                  driver.name,
                                  driver.firstName,
                                  driver.lastName,
                                )
                                const vehicleName =
                                  driverToVehicleName.get(driver.jid) || "-"

                                return (
                                  <TableRow key={driver.jid}>
                                    <TableCell>
                                      <Avatar className="h-8 w-8">
                                        {photoSrc && (
                                          <AvatarImage src={photoSrc} />
                                        )}
                                        <AvatarFallback className="text-xs">
                                          {initials}
                                        </AvatarFallback>
                                      </Avatar>
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {displayName}
                                    </TableCell>
                                    <TableCell>{driver.phone || "-"}</TableCell>
                                    <TableCell>
                                      {driver.preferredLanguage
                                        ? (languageFlags[
                                            driver.preferredLanguage
                                          ] ??
                                          driver.preferredLanguage.toUpperCase())
                                        : "-"}
                                    </TableCell>
                                    <TableCell>{vehicleName}</TableCell>
                                    <TableCell>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditingDriver(driver)}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                )
                              })
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {editingDriver && (
        <EditDriverProfileModal
          isOpen={!!editingDriver}
          onClose={() => setEditingDriver(null)}
          driver={editingDriver}
        />
      )}
      <EditDriverProfileModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        driver={null}
      />
    </>
  )
}

export default DriverManagement
